import React, { createContext, useEffect, useReducer, useState, useContext } from "react";
import { auth } from "../firebase";
import accountReducer from "../store/accountReducer";
import { LOGIN, LOGOUT } from "../store/actions";

import {
	GoogleAuthProvider,
	createUserWithEmailAndPassword,
	onAuthStateChanged,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	signInWithPopup,
	signOut,
	sendEmailVerification
} from "firebase/auth";


type Context = {
  createUser: any;
  users: any;
  logout: any;
  signIn: any;
  token: any;
  googleSignIn: any;
  forgotPassword: any;
  sendEmailVerificationToFirebase: any;
  setUserDetails: any;
  userDetails: any;
  setIsAnnouncementFetch: any;
  isAnnouncementFetch: any;
  isLoading: boolean;
  isGoogleSignInProgress: boolean;
};

const initialState = {
	isLoggedIn   : false,
	isInitialized: false,
	user         : null
};


const UserContext = createContext({} as Context);

export const AuthContextProvider = ({ children }: any) => {
  
	const [ users, setUsers ] = useState<any>({});
	const [ state, dispatch ] = useReducer(accountReducer, initialState);
	const [ token, setToken ] = useState<any>({});
	const [ userDetails, setUserDetails ] = useState<any>({});
	const [ isAnnouncementFetch, setIsAnnouncementFetch ] = useState(false);
	const [ isLoading, setIsLoading ] = useState(true);
	const [ isGoogleSignInProgress, setIsGoogleSignInProgress ] = useState(false);
  
	useEffect(() => {

		const handleTokenRefresh = setInterval(async () => {

			const user = auth.currentUser;
			if (user) {

				/*
				 * getIdToken(true) forces a refresh if expired or close to expiry
				 * You can simply call this; SDK handles the optimization
				 */
				await user.getIdToken(false).catch(err => console.error("Token refresh failed", err));
			
			}
			// Check every 10 mins
		
		}, 10 * 60 * 1000);

		return () => clearInterval(handleTokenRefresh);

	}, []);

	const createUser = (email: any, password: any) => {

		return createUserWithEmailAndPassword(auth, email, password);

	};

	const signIn = async (email: any, password: any) => {

		const res = await signInWithEmailAndPassword(auth, email, password);

		if (auth && auth.currentUser) {

			if (auth.currentUser.emailVerified === false) {

				return;

			} else {

				dispatch({
					type   : LOGIN,
					payload: {
						isLoggedIn: true,
						user      : auth.currentUser
					}

				});

				return res;

			}

		}

	};

	/**
	 * Google Sign-In using popup flow
	 *
	 * Using signInWithPopup for better user experience.
	 * The popup flow opens Google sign-in in a popup window.
	 */
	const googleSignIn = async () => {

		const provider = new GoogleAuthProvider();
		
		// Add scopes if needed
		provider.addScope("email");
		provider.addScope("profile");
		
		// Set custom parameters if needed
		provider.setCustomParameters({
			prompt: "select_account"
		});

		try {

			setIsGoogleSignInProgress(true);

			const result = await signInWithPopup(auth, provider);

			// Extract credential and user data
			const credential = GoogleAuthProvider.credentialFromResult(result);
			const token = credential?.accessToken;
			const user = result.user;

			console.info("✅ Google sign-in successful");
			console.info("User:", user.email);
			console.info("Access Token:", token ? "Present" : "Missing");

			// Set user data
			setUsers(user);
			setIsGoogleSignInProgress(false);

			return result;

		} catch (error: any) {

			setIsGoogleSignInProgress(false);

			console.error("❌ Google sign-in error:", error);
			console.error("Error code:", error?.code);
			console.error("Error message:", error?.message);

			// Try to extract credential from error
			if (error?.customData?.email) {

				console.error("Account email:", error.customData.email);

			}

			const credential = GoogleAuthProvider.credentialFromError(error);

			if (credential) {

				console.error("Credential type:", credential.signInMethod);

			}

			throw error;

		}

	};

	const logout = async () => {

		setUsers({});
		dispatch({
			type: LOGOUT
		});

		setToken({});
    
		await signOut(auth);

	};

	const forgotPassword = (email: any) => {

		return sendPasswordResetEmail(auth, email);

	};

	const sendEmailVerificationToFirebase = () => {

		if (auth && auth.currentUser) {

			return sendEmailVerification(auth?.currentUser);

		}

	};

	useEffect(() => {

		const unsubscribe = onAuthStateChanged(auth, async (currentUser: any) => {

			console.info("Auth state changed:", {
				hasUser      : !!currentUser,
				email        : currentUser?.email,
				emailVerified: currentUser?.emailVerified,
				providers    : currentUser?.providerData?.map((p: any) => p.providerId)
			});

			if (currentUser) {

				setUsers(currentUser);

				// Check if it's a Google sign-in or email verification is not required
				const isGoogleUser = currentUser.providerData?.some((provider: any) => provider.providerId === "google.com");
				const shouldLogin = isGoogleUser || currentUser.emailVerified === true;

				console.info("Login decision:", { isGoogleUser, shouldLogin });

				if (shouldLogin) {

					dispatch({
						type   : LOGIN,
						payload: {
							isLoggedIn: true,
							user      : currentUser
						}

					});

					// Clear loading states after successful login
					setIsLoading(false);
					setIsGoogleSignInProgress(false);
					localStorage.removeItem("googleSignInRedirect");

					console.info("User logged in successfully");

				} else if (currentUser.emailVerified === false) {

					// Email not verified for email/password sign-in
					dispatch({
						type: LOGOUT
					});

					setIsLoading(false);
					setIsGoogleSignInProgress(false);

					console.info("User logged out - email not verified");

				}

			} else {

				dispatch({
					type: LOGOUT
				});

				setIsLoading(false);
				setIsGoogleSignInProgress(false);

				console.info("No user - logged out");
			
			}


		});

		/*
		 * Only unsubscribe from the listener, don't dispatch LOGOUT
		 * This prevents logout on hard refresh when component unmounts and remounts
		 */
		return () => {

			unsubscribe();

		};

	}, []);


	useEffect(() => {

		if (users && users.accessToken) {

			users.getIdTokenResult().then((tokenDetails: any) => {

				setToken(tokenDetails.token);

				localStorage.setItem("fbToken", tokenDetails.token);

			});

		}


	}, [ users, state.isLoggedIn ]);

	return (
		<UserContext.Provider value={{ ...state, createUser, token, users, logout, signIn, googleSignIn, forgotPassword, sendEmailVerificationToFirebase, setUserDetails, userDetails, setIsAnnouncementFetch, isAnnouncementFetch, isLoading, isGoogleSignInProgress }}>
			{children}
		</UserContext.Provider>
	);

};

export const UserAuth = () => {

	return useContext(UserContext);

};

export default UserContext;
